type BatchedSetter<TArgs extends unknown[]> = {
  (...args: [...TArgs, { [key: string]: string | number }]): void;
  /**
   * Flushes a pending batch immediately and clears the debounce timer.
   * Safe to call when no batch is pending.
   */
  flush: () => void;
};

/**
 * Batches rapid analytics property updates into a single callback.
 * Intended for feature-flag hooks so multiple evaluations on mount
 * collapse into one request.
 *
 * @param flush Callback invoked with all accumulated properties once the
 *              quiet period (2000 ms) expires, or immediately when `.flush()`
 *              is called.
 * @returns A batched setter with a `.flush()` method.
 */
export const createBatchedAnalyticsSetter = <TArgs extends unknown[]>(
  flush: (...args: [...TArgs, { [key: string]: string | number }]) => void,
): BatchedSetter<TArgs> => {
  let batchedProps: { [key: string]: string | number } = {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  let latestArgs: TArgs | null = null;

  const flushPending = () => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
    const props = batchedProps;
    const identity = latestArgs!;
    batchedProps = {};
    latestArgs = null;
    flush(...identity, props);
  };

  const setter = (...args: [...TArgs, { [key: string]: string | number }]) => {
    // The properties object is always the last argument.
    const properties = args[args.length - 1] as {
      [key: string]: string | number;
    };
    // Everything before the last argument is the identity args.
    latestArgs = args.slice(0, -1) as unknown as TArgs;

    batchedProps = { ...batchedProps, ...properties };

    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const props = batchedProps;
      const identity = latestArgs!;
      batchedProps = {};
      latestArgs = null;
      flush(...identity, props);
    }, 2000);
  };

  setter.flush = flushPending;

  return setter;
};
